import Button from 'components/ui/Button/Button';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import { useContext, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BranchB2CModel, UserB2CModel } from 'ts/shared/classes/UserB2CModel';
import {
  faBuilding,
  faCompactDisc,
  faEnvelope,
  faLocationDot,
  faMobile,
  faPenToSquare,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { faPhoneOffice } from '@fortawesome/sharp-duotone-solid-svg-icons';
import {
  userB2CValidation as uValidation,
  branchB2CValidation as bValidation,
} from 'constants/constant';
import { showError, showSuccess } from 'utils/utils';
import { validate } from 'utils/validation';
import { getAgencyInfo, getCurrencyBanner, updateAgencyInfo } from 'services/user';
import { CurrencyBanner } from 'ts/shared/interfaces/User';
import withErrorBoundary from './withErrorBoundary';
import { dateFormat } from 'utils/shared/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MainContext } from 'contexts/MainContext';

const ProfilePage = () => {
  const { t } = useTranslation();
  const { cdnUrl } = useContext(MainContext);
  const { user: userContext } = useContext(MainContext);
  const [user, setUser] = useState(new UserB2CModel());
  const [branch, setBranch] = useState(new BranchB2CModel());
  const [isLoading, setIsLoading] = useState(true);
  const [currencies, setCurrencies] = useState<CurrencyBanner[]>();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const showCurrencies = ['EUR', 'GBP', 'USD'];
  const [currentLogo, setCurrentLogo] = useState<string>('');
  const [editMode, setEditMode] = useState(false);
  const isIsrael = ['IL', ''].includes(userContext?.department?.nationalityCode);

  useEffect(() => {
    (async () => {
      const agencyInfo = await getAgencyInfo();
      if (agencyInfo) {
        const userModel = new UserB2CModel(agencyInfo.user);
        const branchModel = new BranchB2CModel(agencyInfo.branch);

        setUser(userModel);
        setBranch(branchModel);

        if (agencyInfo.branch.logoName) {
          setCurrentLogo(
            cdnUrl +
            '/backend/nocache/prod.beprotravel/cdn/logo/' +
            agencyInfo.branch?.logoName +
            '?v=' +
            new Date().getTime()
          );
        }

        setIsLoading(false);
      }
    })();
    (async () => {
      const currencyBanner = await getCurrencyBanner();
      if (currencyBanner) {
        setCurrencies(currencyBanner);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      showError('File type is not valid');
      return;
    }

    const maxSize = 102400;
    if (file.size > maxSize) {
      showError('File size is too large');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64String = e.target?.result as string;
      const mimeType = file.type;

      setBranch({
        ...branch,
        logo: {
          base64File: base64String.split(',')?.[1] || base64String,
          mimeType,
        },
      });

      setLogoPreview(base64String);
    };

    reader.onerror = () => {
      showError('Error reading file');
    };

    reader.readAsDataURL(file);
  };

  const onClickSave = async () => {
    setIsSubmitted(true);
    const validUser = validate(uValidation, user);
    const validBranch = validate(bValidation, branch);
    if (validUser && validBranch) {
      setIsLoading(true);
      const response = await updateAgencyInfo(user, branch);

      if (!response?.hasErrors) {
        showSuccess(t('mo.data_saved'));
      } else {
        showError(t('something_went_wrong'));
      }

      setIsSubmitted(false);
      setIsLoading(false);
    }
  };

  return (
    <div className="details-wrapper profile-wrapper">
      <div className="details">
        <div className="details-block default-shadow">
          <div className="title-underline text-lg mb-20 flex-between">
            <span>
              {t('mo.agency')} / {t('mo.branch')}
            </span>
            <FontAwesomeIcon
              icon={faPenToSquare}
              onClick={() => setEditMode(!editMode)}
              size="sm"
              className="cur-poi"
            />
          </div>
          <div className="row">
            <FormGroup
              className="col-md-6 col-12 mb-20"
              label={'Sabre PCC'}
              value={branch.sbr_pcc}
              onChange={(city) => setBranch({ ...branch, city })}
              movablePlaceholder="Sabre PCC"
              disabled={true}
              movablePlaceholderIcon={faCompactDisc}
            />
            <FormGroup
              className="col-md-6 col-12 mb-20"
              label={t('passenger.city')}
              value={branch.city}
              onChange={(city) => setBranch({ ...branch, city })}
              movablePlaceholder={t('passenger.city')}
              movablePlaceholderIcon={faLocationDot}
              validation={bValidation.city}
              isSubmitted={isSubmitted}
            />
            <FormGroup
              className="col-md-6 col-12 mb-20"
              label={t('passenger.address')}
              value={branch.address}
              onChange={(address) => setBranch({ ...branch, address })}
              movablePlaceholder={t('hotel.address')}
              movablePlaceholderIcon={faBuilding}
              validation={bValidation.address}
              isSubmitted={isSubmitted}
            />
            <FormGroup
              className="col-md-6 col-12 mb-20"
              label={t('passenger.phone')}
              value={branch.tel1}
              onChange={(tel1) => setBranch({ ...branch, tel1, tel2: tel1 })}
              movablePlaceholder={t('passenger.phone')}
              movablePlaceholderIcon={faPhoneOffice as any}
              validation={bValidation.tel1}
              isSubmitted={isSubmitted}
            />
            <FormGroup
              className="col-md-6 col-12 mb-20"
              label={t('passenger.email')}
              value={branch.email1}
              onChange={(email1) => setBranch({ ...branch, email1, email2: email1 })}
              movablePlaceholder={t('passenger.email')}
              movablePlaceholderIcon={faEnvelope}
              validation={bValidation.email1}
              isSubmitted={isSubmitted}
            />
            <FormGroup
              className="col-md-6 col-12 mb-20"
              label={t('passenger.branch_title')}
              value={branch.branchTitle}
              onChange={(branchTitle) =>
                setBranch({ ...branch, branchTitle, branchDesc: branchTitle })
              }
              movablePlaceholder={t('passenger.branch_title')}
              movablePlaceholderIcon={faBuilding}
              validation={bValidation.branchTitle}
              isSubmitted={isSubmitted}
            />
            <div className="col-md-6 col-12 mb-20">
              <Checkbox
                label={t('passenger.display_cancellation_policy')}
                checked={branch.displayOnOrderCP}
                onChange={(value) => setBranch({ ...branch, displayOnOrderCP: value })}
              />
            </div>
            <div className="col-md-6"></div>
            {editMode ? (
              <div className="col-md-6 col-12 mb-20">
                <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif"
                    onChange={handleLogoUpload}
                    style={{ display: 'none' }}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={() => fileInputRef.current?.click()}
                    type="button"
                  >
                    {t('mo.upload_logo')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="col-md-6"></div>
            )}
            <div className="col-md-6"></div>
            <div className="col-md-6 col-12 mb-20">
              {currentLogo && (
                <div className="">
                  <div className="mb-5">{t('mo.current_logo')}</div>
                  <img src={currentLogo} alt="Current logo" className="profile-logo" />
                </div>
              )}
              {logoPreview && (
                <div className="mt-10">
                  <div className="mb-5">{t('mo.new_logo')}</div>
                  <img src={logoPreview} alt="Logo preview" className="profile-logo" />
                </div>
              )}
            </div>
          </div>
          <div className="title-underline text-lg mb-20">{t('mo.user')}</div>
          <div className="row">
            <FormGroup
              className="col-md-6 col-12 mb-20"
              label={t('passenger.fn')}
              value={user.firstName}
              onChange={(firstName) => setUser({ ...user, firstName })}
              movablePlaceholder={t('passenger.fn')}
              movablePlaceholderIcon={faUser}
              validation={uValidation.firstName}
              isSubmitted={isSubmitted}
            />
            <FormGroup
              className="col-md-6 col-12 mb-20"
              label={t('passenger.ln')}
              value={user.lastName}
              onChange={(lastName) => setUser({ ...user, lastName })}
              movablePlaceholder={t('passenger.ln')}
              movablePlaceholderIcon={faUser}
              validation={uValidation.lastName}
              isSubmitted={isSubmitted}
            />
            <FormGroup
              className="col-md-6 col-12 mb-20"
              label={t('passenger.email')}
              value={user.email}
              onChange={(email) => setUser({ ...user, email })}
              movablePlaceholder={t('passenger.email')}
              movablePlaceholderIcon={faEnvelope}
              validation={uValidation.email}
              isSubmitted={isSubmitted}
            />
            <FormGroup
              className="col-md-6 col-12 mb-20"
              label={t('passenger.phone')}
              value={user.workTel}
              onChange={(workTel) => setUser({ ...user, workTel })}
              movablePlaceholder={t('passenger.phone')}
              movablePlaceholderIcon={faPhoneOffice as any}
              validation={uValidation.workTel}
              isSubmitted={isSubmitted}
            />
            <FormGroup
              className="col-md-6 col-12 mb-20"
              label={t('passenger.phone')}
              value={user.mobile}
              onChange={(mobile) => setUser({ ...user, mobile })}
              movablePlaceholder={t('passenger.phone')}
              movablePlaceholderIcon={faMobile}
              validation={uValidation.mobile}
              isSubmitted={isSubmitted}
            />
          </div>
          {editMode && <Button title={t('save')} onClick={onClickSave} disabled={isLoading} />}
        </div>
      </div>
      <div className="details-summary default-shadow">
        <div className="title-underline text-lg mb-20">{t('mo.currencies')}</div>
        <div className="table-default table-small mb-10 text-sm">
          <table className="table-bordered-lines table-mobile">
            <thead>
              <tr>
                <th>{t('mo.currency')}</th>
                <th>USD</th>
                <th>EUR</th>
                <th>GBP</th>
                {isIsrael && <th>ILS</th>}
                <th>{t('mo.last_update')}</th>
              </tr>
            </thead>
            <tbody>
              {currencies
                ?.filter((c) => showCurrencies.includes(c.sysCurrencyCode))
                .map((c) => (
                  <tr key={c.sysCurrencyCode}>
                    <td>{c.sysCurrencyCode}</td>
                    <td>{c.convert_usd_rate?.toFixed(3)}</td>
                    <td>{c.convert_eur_rate?.toFixed(3)}</td>
                    <td>{c.convert_gbp_rate?.toFixed(3)}</td>
                    {isIsrael && <td>{c.convert_loc_rate?.toFixed(3)}</td>}
                    <td>{dateFormat(c.lastUpdateDate, 'dd/MM/yy')}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default withErrorBoundary(ProfilePage, 'ProfilePage');
